import { useState, useCallback } from "react";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import PhoneStep from "./PhoneStep";
import OTPStep from "./OTPStep";
import ProfileStep from "./ProfileStep";
import type { APIError } from "@/services/authApi";

export default function LoginModal() {
  const {
    loginModalOpen,
    loginStep,
    phoneData,
    showGuestOption,
    closeLogin,
    handleSendOTP,
    handleVerifyOTP,
    handleCompleteProfile,
    handleSkipProfile,
    goBackToPhone,
    customerMobile,
  } = useCustomerAuth();
  const { language } = useLanguage();
  const isAr = language === "ar";

  const [phoneLoading, setPhoneLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [remainingAttempts, setRemainingAttempts] = useState<number | undefined>();

  const [lastMobile, setLastMobile] = useState("");
  const profilePhoneNumber = phoneData?.mobile || customerMobile || lastMobile;

  const onSendOTP = useCallback(async (countryCode: string, mobile: string) => {
    setPhoneError(null);
    setPhoneLoading(true);
    setLastMobile(mobile);
    try {
      const result = await handleSendOTP(countryCode, mobile);
      if (!result.success) {
        const err = result as APIError;
        if (err.error.code === "RATE_LIMITED") {
          setPhoneError(
            isAr
              ? `طلبات كثيرة. حاول مرة أخرى بعد ${err.error.retry_after} ثانية`
              : `Too many requests. Try again in ${err.error.retry_after}s`
          );
        } else {
          setPhoneError(err.error.message);
        }
      }
    } finally {
      setPhoneLoading(false);
    }
  }, [handleSendOTP, isAr]);

  const onVerifyOTP = useCallback(async (otp: string) => {
    setOtpError(null);
    setOtpLoading(true);
    try {
      const result = await handleVerifyOTP(otp);
      if (!result.success) {
        const err = result as APIError;
        setRemainingAttempts(err.error.remaining_attempts);
        if (err.error.code === "INVALID_OTP") {
          setOtpError(isAr ? "الرمز غير صحيح" : "Incorrect code");
        } else if (err.error.code === "OTP_EXPIRED") {
          setOtpError(isAr ? "انتهت صلاحية الرمز. اطلب رمزاً جديداً" : "Code expired. Request a new one");
        } else if (err.error.code === "MAX_ATTEMPTS") {
          setOtpError(isAr ? "تم تجاوز عدد المحاولات" : "Too many attempts");
        } else {
          setOtpError(err.error.message);
        }
      }
    } finally {
      setOtpLoading(false);
    }
  }, [handleVerifyOTP, isAr]);

  const onResendOTP = useCallback(async () => {
    if (!phoneData) return;
    setOtpError(null);
    await handleSendOTP(phoneData.country_code, phoneData.mobile);
  }, [phoneData, handleSendOTP]);

  const onSaveProfile = useCallback(async (firstName: string, lastName: string, phoneNumber: string, email?: string) => {
    setProfileLoading(true);
    try {
      await handleCompleteProfile(firstName, lastName, phoneNumber, email);
    } finally {
      setProfileLoading(false);
    }
  }, [handleCompleteProfile]);

  const handleBack = useCallback(() => {
    setOtpError(null);
    setRemainingAttempts(undefined);
    goBackToPhone();
  }, [goBackToPhone]);

  return (
    <Dialog open={loginModalOpen} onOpenChange={(open) => { if (!open && loginStep !== "profile") closeLogin(); }}>
      <DialogContent className="sm:max-w-md p-6" onPointerDownOutside={loginStep === "profile" ? (e) => e.preventDefault() : undefined} onEscapeKeyDown={loginStep === "profile" ? (e) => e.preventDefault() : undefined} hideCloseButton={loginStep === "profile"}>
        <DialogTitle className="sr-only">
          {isAr ? "تسجيل الدخول" : "Login"}
        </DialogTitle>

        {loginStep === "phone" && (
          <PhoneStep
            onSubmit={onSendOTP}
            loading={phoneLoading}
            error={phoneError}
            showGuestOption={showGuestOption}
            onContinueAsGuest={closeLogin}
            initialMobile={lastMobile}
          />
        )}

        {loginStep === "otp" && phoneData && (
          <OTPStep
            maskedNumber={phoneData.masked_number}
            otpLength={phoneData.otp_length}
            onVerify={onVerifyOTP}
            onResend={onResendOTP}
            onBack={handleBack}
            loading={otpLoading}
            error={otpError}
            remainingAttempts={remainingAttempts}
          />
        )}

        {loginStep === "profile" && (
          <ProfileStep
            phoneNumber={profilePhoneNumber}
            onSave={onSaveProfile}
            onSkip={handleSkipProfile}
            loading={profileLoading}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
